import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, parseErrorStatus } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export type CleaningPreview = {
  id: string; action: string; expiresAt: string; timezone: string; sendAt: string;
  automation: CleaningAutomationPreview | null; status: string;
  recipients: Array<{ name: string; lifecycleStatus: string; channel: string; destination: string; body: string; subject?: string; formUrl?: string }>;
  results?: unknown;
};

export type CleaningAutomationPreview = {
  timezone: string;
  launch: unknown;
  messages: Array<{
    kind: "launch" | "day_of" | "follow_up" | "introduction";
    memberId: number | null;
    name: string;
    body: string;
    scheduledAt: string | null;
    timezone: string;
    status: "scheduled" | "conditional" | "suppressed";
    reason: string | null;
  }>;
};

function propertyTime(value: string, timezone: string) {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "full", timeStyle: "long", timeZone: timezone,
  }).format(new Date(value));
}

const AUTOMATION_LABELS = {
  launch: "Launch announcement",
  day_of: "Day-of reminder",
  follow_up: "Follow-up reminder",
  introduction: "New-member introduction",
};

/** Server-rendered content and scheduling decisions; never reconstruct messages locally. */
export function CleaningAutomationMessages({ automation }: { automation: CleaningAutomationPreview }) {
  return <section className="space-y-3" data-testid="cleaning-automation-messages">
    <p className="text-sm">Property timezone: {automation.timezone}</p>
    <p className="text-xs text-muted-foreground">Read-only preview. No messages are sent and no proof links are created. Follow-up estimates depend on the actual day-of send and valid proof still being absent.</p>
    <div className="text-sm"><h4 className="font-medium">Launch status</h4><ReadableDetails value={automation.launch} /></div>
    {automation.messages.length === 0 && <p className="text-sm">No automatic messages to preview.</p>}
    {automation.messages.map((message, index) => <section key={`${message.kind}-${message.memberId}-${index}`} className="rounded border p-3 space-y-2">
      <h4 className="font-medium">{AUTOMATION_LABELS[message.kind]} · {message.name}</h4>
      <p className="text-sm capitalize">Status: {message.status}</p>
      <p className="text-sm">{message.scheduledAt
        ? `${message.status === "conditional" ? "Estimated send time" : message.status === "suppressed" ? "Suppressed send time (will not send)" : "Scheduled send time"}: ${propertyTime(message.scheduledAt, message.timezone)} · ${message.timezone}`
        : "Send time: not scheduled"}</p>
      {message.reason && <p className="text-sm text-muted-foreground">{message.reason}</p>}
      <pre className="whitespace-pre-wrap break-words font-sans text-sm">{message.body}</pre>
    </section>)}
  </section>;
}

export function CleaningAutomationPreviewButton({ propertyId, disabled }: { propertyId: number | string; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const url = `/api/app/properties/${propertyId}/cleaning/automation-preview`;
  const query = useQuery<CleaningAutomationPreview>({
    queryKey: [url],
    queryFn: async () => {
      const data = await bounded(apiRequest("GET", url).then((response) => response.json()));
      if (!Array.isArray(data?.messages) || typeof data?.timezone !== "string") throw new Error("Invalid automation preview response");
      return data;
    },
    enabled: open,
    staleTime: 0,
  });
  return <>
    <Button type="button" variant="outline" disabled={disabled} onClick={() => { setOpen(true); }} data-testid="button-cleaning-automation-preview">Preview saved notification schedule</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Saved cleaning notification schedule</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Uses saved settings and current assignments, not unsaved edits. To review a new activation, choose Preview activation.</p>
        {query.isFetching && <p role="status">Loading read-only preview…</p>}
        {query.isError && <p role="alert">Could not load the notification schedule. Nothing was sent. <Button variant="outline" onClick={() => query.refetch()}>Try again</Button></p>}
        {!query.isFetching && !query.isError && query.data && <CleaningAutomationMessages automation={query.data} />}
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>Close</Button>
      </DialogContent>
    </Dialog>
  </>;
}

async function bounded<T>(request: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  try {
    return await Promise.race([request, new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error("Request timed out. Check notification history; do not resend an uncertain delivery.")), 30000);
    })]);
  } finally { clearTimeout(timer!); }
}

function ReadableDetails({ value }: { value: unknown }) {
  if (value == null) return <span>None</span>;
  if (typeof value === "boolean") return <span>{value ? "Enabled" : "Disabled"}</span>;
  if (typeof value !== "object") return <span className="whitespace-pre-wrap break-words">{String(value)}</span>;
  if (Array.isArray(value)) return <ul className="space-y-2">{value.map((item, i) => <li key={i}><ReadableDetails value={item} /></li>)}</ul>;
  return <dl className="space-y-1">{Object.entries(value).map(([key, item]) => (
    <div key={key} className="flex flex-wrap gap-x-2">
      <dt className="font-medium capitalize">{key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ")}:</dt>
      <dd className="min-w-0"><ReadableDetails value={item} /></dd>
    </div>
  ))}</dl>;
}

/** Preview requests cannot send. Only the explicit confirmation button dispatches. */
export function useCleaningConfirmation(propertyId: number | string, onConfirmed?: () => void) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<CleaningPreview | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const generation = useRef(0);
  const previewBody = useRef<Record<string, unknown> | null>(null);
  const queryClient = useQueryClient();
  const base = `/api/app/properties/${propertyId}/cleaning`;
  const close = () => {
    generation.current++;
    setOpen(false);
    setPreview(null);
    setError("");
  };
  useEffect(() => {
    window.addEventListener("popstate", close);
    return () => { generation.current++; window.removeEventListener("popstate", close); };
  }, [propertyId]);
  const requestPreview = async (body: Record<string, unknown>) => {
    if (lock.current) return;
    lock.current = true;
    previewBody.current = body;
    const version = ++generation.current;
    setBusy(true); setError(""); setPreview(null); setOpen(true);
    try {
      const data = await bounded(apiRequest("POST", `${base}/message-previews`, body).then((res) => res.json()));
      if (version === generation.current) setPreview(data);
    } catch (e) {
      if (version === generation.current) setError(parseErrorMessage(e, "Could not load preview. Nothing was sent. Close and try again."));
    } finally { lock.current = false; setBusy(false); }
  };
  const confirm = async () => {
    if (!preview || lock.current || preview.status !== "preview") return;
    if (preview.action === "activation" && (!Array.isArray(preview.automation?.messages) || preview.recipients.length > 0)) return;
    lock.current = true; setBusy(true); setError("");
    try {
      const result = await bounded(apiRequest("POST", `${base}/message-previews/${preview.id}/confirm`, {}).then((res) => res.json()));
      setPreview({ ...preview, ...result });
      void queryClient.invalidateQueries({ queryKey: [`${base}/notification-history`] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      if (result.status === "completed") onConfirmed?.();
    } catch (e) {
      // A transport failure may follow dispatch. Never enable a new send blindly.
      const stale = parseErrorStatus(e) === 409;
      setPreview({ ...preview, status: stale ? "stale" : "uncertain" });
      setError(stale ? "This preview is stale or expired. Review a fresh preview before sending." : parseErrorMessage(e, "Confirmation could not be verified. Check notification history before taking further action."));
      queryClient.invalidateQueries({ queryKey: [`${base}/notification-history`] });
    } finally { lock.current = false; setBusy(false); }
  };
  return {
    requestPreview, busy, open,
    dialog: <Dialog open={open} onOpenChange={(value) => { if (!value && !busy) close(); }}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cleaning-message-preview"
        onEscapeKeyDown={(event) => { if (busy) event.preventDefault(); }}
        onInteractOutside={(event) => { if (busy) event.preventDefault(); }}
        onPointerDownOutside={(event) => { if (busy) event.preventDefault(); }}>
        <DialogHeader><DialogTitle>{preview?.action === "activation" ? "Review scheduled cleaning automation" : "Review cleaning message"}</DialogTitle></DialogHeader>
        {busy && <p role="status">{preview ? "Confirming… Check history if you leave this screen." : "Loading preview — nothing has been sent."}</p>}
        {error && <p role="alert" className="text-destructive">{error}</p>}
        {preview && <>
          <p className="text-sm">{preview.action === "activation" ? "Activation does not send any messages immediately." : `Send time: ${preview.sendAt}`} · Property timezone: {preview.timezone}</p>
          <p className="text-xs text-muted-foreground">Preview expires: {new Date(preview.expiresAt).toLocaleString()}</p>
          {preview.action === "activation" && <section className="space-y-2"><h3 className="font-medium">Scheduled automation enabled after confirmation</h3>{preview.automation && Array.isArray(preview.automation.messages)
            ? <CleaningAutomationMessages automation={preview.automation} />
            : <p role="alert">Scheduled message details are unavailable. Close and request a fresh activation preview.</p>}</section>}
          {preview.recipients.length === 0 && <p>No immediate message recipients. Confirming applies the reviewed action and automation.</p>}
          {preview.action === "activation" && preview.recipients.length > 0 && <p role="alert">This activation preview contains immediate sends and cannot be confirmed. Request a fresh preview.</p>}
          {preview.action !== "activation" && preview.recipients.map((recipient, i) => <section key={i} className="rounded border p-3 space-y-2">
            <p className="font-medium">{recipient.name} · {recipient.lifecycleStatus}</p>
            <p className="text-sm break-all">{recipient.channel} · {recipient.destination}</p>
            {recipient.subject && <p>{recipient.subject}</p>}
            <pre className="whitespace-pre-wrap font-sans text-sm">{recipient.body}</pre>
            {recipient.formUrl && <p className="text-xs break-all">Form link: {recipient.formUrl}</p>}
          </section>)}
          {preview.status !== "preview" && <div role="status"><p className="font-medium">Result: {preview.status}</p><p className="text-sm">Queued means accepted, not delivered. Failed or uncertain messages remain in notification history.</p><div className="text-sm"><ReadableDetails value={preview.results} /></div></div>}
        </>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={close}>{preview && preview.status !== "preview" ? "Close" : "Cancel"}</Button>
          {(preview?.status === "stale" || (preview?.status === "preview" && Date.parse(preview.expiresAt) <= Date.now())) && <Button disabled={busy} variant="outline" onClick={() => previewBody.current && requestPreview(previewBody.current)}>Refresh preview</Button>}
          <Button onClick={confirm} disabled={busy || !preview || preview.status !== "preview" || Date.parse(preview.expiresAt) <= Date.now() || (preview.action === "activation" && (!Array.isArray(preview.automation?.messages) || preview.recipients.length > 0))}>{preview?.action === "activation" ? "Enable scheduled automation" : "Send now"}</Button>
        </div>
      </DialogContent>
    </Dialog>,
  };
}

export function CleaningNotificationHistory({ propertyId }: { propertyId: number | string }) {
  const retry = useCleaningConfirmation(propertyId);
  const url = `/api/app/properties/${propertyId}/cleaning/notification-history`;
  type HistoryEntry = { id: string | number; status: string; canRetry?: boolean; retryBlockedReason?: string | null; [key: string]: unknown };
  const history = useQuery<{ history: HistoryEntry[]; automatic?: HistoryEntry[]; nextSend: unknown; lastResult: unknown }>({
    queryKey: [url],
    queryFn: async () => {
      const data = await bounded(apiRequest("GET", url).then((res) => res.json()));
      if (!Array.isArray(data?.history)) throw new Error("Invalid notification history response");
      return data;
    },
    refetchInterval: 15000,
  });
  return <section className="space-y-2 rounded border p-3" data-testid="cleaning-notification-history">
    <h3 className="font-medium">Notification history</h3>
    {history.isLoading && <p>Loading notification status…</p>}
    {history.isError && <p role="alert">Could not load notification history. <Button variant="outline" onClick={() => history.refetch()}>Refresh status</Button></p>}
    {history.data && <>
      <div className="text-sm"><h4 className="font-medium">Next send (property timezone)</h4><ReadableDetails value={history.data.nextSend ?? "None scheduled"} /></div>
      <div className="text-sm"><h4 className="font-medium">Last result</h4><ReadableDetails value={history.data.lastResult ?? "No attempts"} /></div>
      <p className="text-xs text-muted-foreground">Queued is not final delivery. Uncertain attempts must not be resent.</p>
      <details><summary className="cursor-pointer text-sm">Manual and automated attempts ({history.data.history.length + (history.data.automatic?.length ?? 0)})</summary>
        {[...history.data.history, ...(history.data.automatic ?? [])].map((entry, i) => <div key={i} className="border-b py-2 text-sm space-y-2">
          <ReadableDetails value={Object.fromEntries(Object.entries(entry).filter(([key]) => key !== "canRetry" && key !== "retryBlockedReason"))} />
          {entry.canRetry === true && entry.status === "failed"
            ? <Button size="sm" variant="outline" disabled={retry.busy} onClick={() => retry.requestPreview({ action: "retry", attemptId: entry.id })}>Preview retry</Button>
            : entry.retryBlockedReason && <p className="text-xs text-muted-foreground">Retry unavailable: {entry.retryBlockedReason}</p>}
        </div>)}
      </details>
    </>}
    {retry.dialog}
  </section>;
}
